import {noop} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {Pill} from '../../../../components/pill/pill';

export type ColumnNames = {[key: string]: ReactNode};

/*
 * Style.
 */

const StyledIconButton = styled(IconButton)`
  margin-left: 8px;
`;

const StyledPrimePill = styled(Pill)`
  margin-left: 4px;
`;

/*
 * Props.
 */

type TableColumnPickerProps = {
  canBeHiddenColumnIds: ReadonlyArray<string>;
  shownColumnIds: ReadonlyArray<string>;
  disabledColumnIds: ReadonlyArray<string>;
  columnNames: ColumnNames;
  onColumnClick: HandlerOf<string>;
  isLoading: boolean;
};

type TableColumnPickerDropdownProps = TableColumnPickerProps;

/*
 * Component.
 */

export const TableColumnPicker: FC<TableColumnPickerProps> = (props) => (
  <PopoverCoordinator
    isDisabled={props.isLoading}
    renderButton={renderButton}
    renderModal={(popoverProps) => renderDropdown(popoverProps, props)}
  />
);

const renderButton = () => (
  <UserThemeConsumer>
    {(theme) => (
      <StyledIconButton
        iconName="gear"
        label={<Trans id="analytics-columns-button">Customize columns</Trans>}
        color={theme.greys.shade70}
        hoverColor={theme.greys.shade70}
      />
    )}
  </UserThemeConsumer>
);

function renderDropdown(popoverTransferProps: PopoverTransferProps, props: TableColumnPickerDropdownProps) {
  const {canBeHiddenColumnIds, shownColumnIds, disabledColumnIds, columnNames, onColumnClick} = props;

  return (
    <Dropdown {...popoverTransferProps}>
      {/* onRequestClose prop is used to keep the menu open when an item is clicked */}
      <DropdownList {...popoverTransferProps} onRequestClose={noop}>
        {canBeHiddenColumnIds.map((columnId) => {
          const isChecked = shownColumnIds.includes(columnId);
          const isDisabled = disabledColumnIds.includes(columnId);

          return (
            <DropdownItem
              key={columnId}
              isChecked={isChecked}
              onSelect={() => onColumnClick(columnId)}
              isDisabled={isDisabled}
            >
              <DropdownItemContent isChecked={isChecked}>
                {columnNames[columnId]}
                {isDisabled && (
                  <StyledPrimePill size={VisualSizesEnum.MEDIUM}>
                    <Trans id="analytics-upsell-table-col-picker-growth">GROWTH</Trans>
                  </StyledPrimePill>
                )}
              </DropdownItemContent>
            </DropdownItem>
          );
        })}
      </DropdownList>
    </Dropdown>
  );
}
